import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Select, TextInput, TileGroup } from "@opendate-app/setlist";

import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import NodeErrorContainer from "../NodeErrorContainer";
import SmsStatusAlert from "bundles/shared/components/SmsStatusAlert";
import SegmentPicker from "../../../../shared/SegmentPicker";
import SegmentAudienceWarningModal from "./SegmentAudienceWarningModal";
import { toastSuccess, toastError } from "../../../../shared/toastHelper";

const ASSIGNED_STAFF = "Contact's assigned staff member";
const ASSIGNED_STAFF_LABEL = "Contact's relationship manager";

const FAN_RECIPIENT = { value: "Fan", label: "Fan", supportingText: "Send to the fan who performed the action" };
const ASSIGNED_STAFF_RECIPIENT = {
  value: ASSIGNED_STAFF,
  label: ASSIGNED_STAFF_LABEL,
  supportingText: "Text the relationship manager assigned to this contact, if they have a phone number",
};
const FAN_SEGMENT_RECIPIENT = { value: "Fan Segment", label: "Fan Segment", supportingText: "Send to an automated segment as defined by the trigger" };
const AD_HOC_RECIPIENT = { value: "Ad Hoc", label: "Ad Hoc", supportingText: "Manually enter your SMS recipient(s)" };

const sectionHeaderStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
};

const sectionTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  color: "var(--sl-text-primary)",
};

const fieldLabelStyle = {
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  color: "var(--sl-text-secondary)",
};

const helpTextStyle = {
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--sl-text-tertiary)",
};

const addContentContainerStyle = {
  background: "var(--sl-bg-tertiary)",
  width: 400,
  maxWidth: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  borderRadius: 4,
  border: "1px dashed var(--sl-border-primary)",
};

const previewContainerStyle = {
  borderRadius: 4,
  border: "1px dashed var(--sl-border-primary)",
  minWidth: 400,
  overflow: "auto",
  marginBottom: 16,
  position: "relative",
};

const templatePreviewStyle = {
  padding: 24,
  maxWidth: 500,
  textOverflow: "ellipsis",
  whiteSpace: "wrap",
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--sl-text-primary)",
};

const floatingButtonContainerStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "color-mix(in srgb, var(--sl-bg-tertiary) 70%, transparent)",
  zIndex: 10,
};

const recipientConfigurationStyle = {
  background: "var(--sl-bg-tertiary)",
  borderRadius: 8,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const SmsNodeForm = () => {
  const {
    csrfToken,
    team,
    customCampaign,
    setCustomCampaign,
    selectedNode,
    setSelectedNode,
    setShowTwilioComposer,
    selectedNodeFormState,
    setSelectedNodeFormState,
    customCampaignBaseUrl,
    smsStatus,
  } = useCustomCampaignShowContext();

  const [isLoading, setIsLoading] = useState(false);

  const buildNodeDataFormState = () => {
    return {
      subscription_preference: selectedNode.node_data.subscription_preference || "",
      recipient_type: selectedNode.node_data.recipient_type || "",
      to: selectedNode.node_data.to || "",
      segment_name: selectedNode.node_data.segment_name || "",
      segment_id: selectedNode.node_data.segment_id || null,
      segment_label: selectedNode.node_data.segment_label || "",
    };
  };

  const createTwilioTemplate = (e) => {
    e.preventDefault();

    if (isLoading) {
      return;
    }

    setIsLoading(true);

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.patch(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`, {
      workflow_node: {
        twilio_template_attributes: {
          team_id: team.id,
        }
      }
    })
    .then(({ data: { workflow_node, custom_campaign } }) => {
      setCustomCampaign(custom_campaign);
      setSelectedNode(workflow_node);
      toastSuccess("SMS template created successfully.");
      setShowTwilioComposer(true);
    })
    .catch((error) => {
      toastError("Unable to create SMS template.");
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  const [nodeDataFormState, setNodeDataFormState] = useState(buildNodeDataFormState());
  const [pendingSegment, setPendingSegment] = useState(null);

  useEffect(() => {
    setNodeDataFormState(buildNodeDataFormState());
  }, [selectedNode]);

  useEffect(() => {
    const debouncedUpdateNodeDataFormState = _.debounce(() => {
      selectedNodeFormState && setSelectedNodeFormState({
        ...selectedNodeFormState,
        node_data: { ...selectedNodeFormState.node_data, ...nodeDataFormState },
      });
    }, 300);

    debouncedUpdateNodeDataFormState();

    return () => {
      debouncedUpdateNodeDataFormState.cancel();
    };
  }, [nodeDataFormState]);

  const updateNodeDataFormState = (newNodeData) => {
    setNodeDataFormState({ ...nodeDataFormState, ...newNodeData });
  };

  const enableFan = () => {
    if (nodeDataFormState.recipient_type === "Fan") {
      return;
    }

    updateNodeDataFormState({
      recipient_type: "Fan",
      to: "+1{{fan.phone_number}}",
      segment_name: "",
      segment_id: null,
      segment_label: "",
      subscription_preference: "Use fan marketing preferences",
    });
  }

  const enableFanSegment = () => {
    if (nodeDataFormState.recipient_type === "Fan Segment") {
      return;
    }

    updateNodeDataFormState({
      recipient_type: "Fan Segment",
      to: "+1{{fan.phone_number}}",
      segment_name: "",
      segment_id: null,
      segment_label: "",
      subscription_preference: "Use fan marketing preferences",
    });
  };

  const enableAssignedStaff = () => {
    if (nodeDataFormState.recipient_type === ASSIGNED_STAFF) {
      return;
    }

    updateNodeDataFormState({
      recipient_type: ASSIGNED_STAFF,
      to: "+1{{fan.phone_number}}",
      segment_name: "",
      segment_id: null,
      segment_label: "",
      subscription_preference: "Ignore fan marketing preferences",
    });
  };

  const enableAdHoc = () => {
    if (nodeDataFormState.recipient_type === "Ad Hoc") {
      return;
    }

    updateNodeDataFormState({
      recipient_type: "Ad Hoc",
      to: "",
      segment_name: "",
      segment_id: null,
      segment_label: "",
      subscription_preference: "Ignore fan marketing preferences",
    });
  };

  const isFanAction = customCampaign.trigger_type === "Fan action";
  const isEventTrigger = customCampaign.trigger_type === "Event" || customCampaign.trigger_type === "System Event";

  const recipientItems = [
    ...(isFanAction ? [FAN_RECIPIENT, ASSIGNED_STAFF_RECIPIENT] : []),
    ...(isEventTrigger ? [FAN_SEGMENT_RECIPIENT] : []),
    AD_HOC_RECIPIENT,
  ];

  const selectRecipientType = (recipientType) => {
    if (recipientType === "Fan") enableFan();
    if (recipientType === ASSIGNED_STAFF) enableAssignedStaff();
    if (recipientType === "Fan Segment") enableFanSegment();
    if (recipientType === "Ad Hoc") enableAdHoc();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {smsStatus && !smsStatus.can_send && (
        <SmsStatusAlert
          variant={smsStatus?.cta?.label === "Update Payment Method" ? "error" : "info"}
          title={smsStatus?.reason}
          ctaLabel={smsStatus?.cta?.label}
          ctaHref={smsStatus?.cta?.path}
        />
      )}
      <div style={sectionHeaderStyle}>
        <div style={sectionTitleStyle}>Content</div>
        <NodeErrorContainer errors={[
          ...(selectedNodeFormState?.workflow_errors.twilio_template || []),
          ...(selectedNodeFormState?.workflow_errors.to || []),
          ...(selectedNodeFormState?.workflow_errors.twilio_messaging_service || []),
        ]} />
      </div>
      {
        selectedNode.twilio_template ? (
          <div style={previewContainerStyle}>
            <div style={templatePreviewStyle}>{selectedNode.twilio_template.template}</div>
            <div style={floatingButtonContainerStyle}>
              <Button size="sm" onClick={() => setShowTwilioComposer(true)}>Edit Content</Button>
            </div>
          </div>
        ) : (
          <div style={addContentContainerStyle}>
            <Button size="sm" disabled={isLoading} onClick={createTwilioTemplate}>Add Content</Button>
          </div>
        )
      }
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={sectionHeaderStyle}>
          <div style={sectionTitleStyle}>Recipient</div>
          <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.recipient_type} />
        </div>
        <div style={helpTextStyle}>Event based triggers require recipient to be defined.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <TileGroup
            aria-label="Recipient"
            columns={1}
            items={recipientItems}
            value={nodeDataFormState.recipient_type || null}
            onChange={selectRecipientType}
          />
          {isFanAction && nodeDataFormState.recipient_type === ASSIGNED_STAFF ? (
            <div style={helpTextStyle}>Staff add their mobile number on their My Profile page. Anyone without one won't get the text.</div>
          ) : null}
          {isEventTrigger && nodeDataFormState.recipient_type === "Fan Segment" ? (
            <div style={recipientConfigurationStyle}>
              <div style={sectionHeaderStyle}>
                <div style={fieldLabelStyle}>Segment *</div>
                <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.segment_name} />
              </div>
              <SegmentPicker
                team={team}
                value={{ segmentId: nodeDataFormState.segment_id, segmentName: nodeDataFormState.segment_name }}
                selectedLabel={
                  (nodeDataFormState.segment_id && selectedNodeFormState?.node_data_options?.saved_segment_label) ||
                  nodeDataFormState.segment_label ||
                  nodeDataFormState.segment_name
                }
                relativeGroup={{
                  label: "Triggering event",
                  options: (selectedNodeFormState?.node_data_options?.relative_audience_names || []).map((name) => ({
                    value: name,
                    label: name,
                  })),
                }}
                onChange={(next) => {
                  if (next.segmentName) {
                    updateNodeDataFormState({ segment_name: next.segmentName, segment_id: null, segment_label: next.segmentName });
                  } else {
                    setPendingSegment(next);
                  }
                }}
              />
            </div>
          ) : null}
          {nodeDataFormState.recipient_type === "Ad Hoc" ? (
            <div style={recipientConfigurationStyle}>
              <div style={sectionHeaderStyle}>
                <div style={fieldLabelStyle}>SMS recipients</div>
                <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.to} />
              </div>
              <TextInput
                aria-label="SMS recipients"
                value={nodeDataFormState.to}
                onChange={(e) => updateNodeDataFormState({ to: e.target.value })}
                placeholder="+1234567890, +1234567890, etc..."
              />
            </div>
          ) : null}
        </div>
      </div>
      { nodeDataFormState.recipient_type !== "Ad Hoc" && nodeDataFormState.recipient_type !== ASSIGNED_STAFF ? (
        <>
          <div style={sectionHeaderStyle}>
            <div style={sectionTitleStyle}>Settings</div>
            <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.settings} />
          </div>
          <Select
            label="Subscription preference"
            placeholder="Select fan subscription preference"
            options={(selectedNodeFormState?.node_data_options?.subscription_preference || []).map((preference) => ({
              value: preference,
              label: preference,
            }))}
            value={nodeDataFormState.subscription_preference || null}
            onChange={(preference) => updateNodeDataFormState({ subscription_preference: preference })}
          />
        </>
      ) : null}
      <SegmentAudienceWarningModal
        pendingSegment={pendingSegment}
        channel="text"
        onCancel={() => setPendingSegment(null)}
        onConfirm={(segment) => {
          updateNodeDataFormState({ segment_id: segment.segmentId, segment_name: "", segment_label: segment.label });
          setPendingSegment(null);
        }}
      />
    </div>
  );
};

SmsNodeForm.propTypes = {};

export default SmsNodeForm;
